import {
  ChangeEventHandler,
  FocusEventHandler,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import Input from '../Input';
import styles from './styles.module.css';
import {
  composeKeyAction,
  keyList,
  useKeyAction,
} from '../../../services/useKeyAction';
import classNames from 'classnames';

type InputProps = React.ComponentProps<typeof Input>;
type Props<T extends object> = {
  list: T[];
  onClickSuggest: (value: T) => void;
  getKey: (value: T) => string | number;
  getLabel: (value: T) => string;
  onChange: (value: string) => void;
} & Omit<InputProps, 'list' | 'defaultValue' | 'onChange'>;

// React Hook Form の register を使用することは想定しないので ref は fowward しない
const SuggestInput = <T extends object>({
  list,
  onBlur,
  onClickSuggest,
  onChange,
  getKey,
  getLabel,
  variants = [],
  ...props
}: Props<T>): ReactElement => {
  const [isVisibleSuggest, setIsVisibleSuggest] = useState(false);
  const [value, setValue] = useState(props.value);
  useEffect(() => {
    setValue(props.value);
  }, [props.value]);
  const inputBlurHandler: FocusEventHandler<HTMLInputElement> = (e) => {
    // そのままサジェストポップアップを消すとサジェスト項目クリック時に
    // クリック判定より先にサジェストポップアップが消えてしまうため、
    // 不自然にならない程度にディレイを持たせる。
    setTimeout(() => {
      setIsVisibleSuggest(false);
      setSelectedIndex(null);
      if (onBlur !== undefined) {
        onBlur(e);
      }
    }, 300);
  };
  const timeoutRef = useRef<NodeJS.Timeout>();
  const onChangeValue = (value: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setValue(value);
    if (onChange !== undefined) {
      timeoutRef.current = setTimeout(() => {
        onChange(value);
        timeoutRef.current = undefined;
      }, 300);
    }
  };
  const inputChangeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    setIsVisibleSuggest(true);
    const value = e.target.value;
    onChangeValue(value);
  };
  const suggestClickHandler = (value: T) => {
    onClickSuggest(value);
  };
  const suggestVariants: InputProps['variants'] = isVisibleSuggest
    ? ['suggest']
    : [];
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const onKeyDown = composeKeyAction<HTMLInputElement>(
    useKeyAction(keyList.DOWN, (e) => {
      // 上下キー操作によるカーソル移動の無効化
      e.preventDefault();
      if (selectedIndex === null) {
        setSelectedIndex(0);
        return;
      }
      setSelectedIndex((value) => Math.min(list.length, (value || 0) + 1));
    }),
    useKeyAction(keyList.UP, (e) => {
      // 上下キー操作によるカーソル移動の無効化
      e.preventDefault();
      if (selectedIndex === null) {
        setSelectedIndex(0);
        return;
      }
      setSelectedIndex((value) => Math.max(0, (value || 0) - 1));
    }),
    useKeyAction(keyList.ENTER, (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (selectedIndex === null) {
        return;
      }
      onClickSuggest(list[selectedIndex]);
      setIsVisibleSuggest(false);
      e.currentTarget.blur();
    })
  );
  useEffect(() => {
    setSelectedIndex(null);
  }, [list.length]);

  return (
    <div className={styles.container}>
      <Input
        {...props}
        value={value}
        onChange={inputChangeHandler}
        onBlur={inputBlurHandler}
        variants={[...suggestVariants, ...variants]}
        onKeyDown={onKeyDown}
      />
      {isVisibleSuggest && (
        <div className={styles.suggest} onClick={(e) => e.stopPropagation()}>
          {list.map((item, index) => (
            <button
              type="button"
              className={classNames(
                styles.suggestItem,
                index === selectedIndex && styles.selected
              )}
              onClick={() => suggestClickHandler(item)}
              key={getKey(item)}
            >
              {getLabel(item)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SuggestInput;
